import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PaymentModal } from "./Payments";
import { useInvoicePilot } from "./InvoicePilotLink";
import { api } from "../lib/api";
import { useOrg } from "../lib/org";
import { Button, ErrorState, Money, PageHeader, Spinner, StatusBadge } from "../components/ui";
import { DataTable } from "../components/DataTable";
import { formatDate } from "../lib/format";
import type { BillListRow } from "../lib/models";

export default function Bills() {
  const { currentOrg } = useOrg();
  const navigate = useNavigate();
  const currency = currentOrg?.baseCurrency ?? "USD";
  const pilot = useInvoicePilot();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [pay, setPay] = useState(false);
  const [receipt, setReceipt] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["bills", currentOrg?.id],
    queryFn: () => api.get<BillListRow[]>("/bills"),
    enabled: !!currentOrg,
  });
  const selected = data?.find(b => b.id === params.get("bill"));
  const receiptKey = `${currentOrg?.id}:${selected?.id}`;

  return (
    <div>
      <PageHeader
        title="Bills"
        subtitle="Money you owe to vendors"
        action={<Button onClick={() => navigate("/bills/new")}>Enter bill</Button>}
      />
      {isLoading && <Spinner />}
      {error && <ErrorState error={error} />}
      {pilot.data?.enabled && selected && <section className="mb-4 rounded-lg border border-brand-100 bg-brand-50 p-4">
        <h2 className="font-semibold">Bill {selected.docNumber} · {selected.vendorName}</h2>
        <p><StatusBadge status={selected.status} /> Remaining balance: <Money cents={selected.balance} currency={currency} /></p>
        <p role="status">{receipt === receiptKey ? (selected.balance > 0 ? "Payment recorded. The remaining balance is shown above." : "Payment recorded. This bill is paid.") : selected.status === "void" ? "This bill is void." : selected.balance > 0 ? "This bill is posted. When you pay the vendor, record the payment here." : "This bill has no remaining balance."}</p>
        <div className="mt-3 flex gap-2">
          {selected.balance > 0 && selected.status !== "void" && <Button onClick={() => setPay(true)}>Record payment</Button>}
          <Button variant="secondary" onClick={() => {setParams({});setPay(false);}}>All bills</Button>
        </div>
      </section>}
      {pilot.data?.enabled && selected && pay && selected.balance > 0 && selected.status !== "void" &&
        <PaymentModal key={`${currentOrg?.id}:${selected.id}`} open bill={selected} onClose={() => setPay(false)} onSaved={() => {
          setPay(false);
          void Promise.all([qc.invalidateQueries({queryKey:["bills",currentOrg?.id]}),qc.invalidateQueries({queryKey:["payments"]})]).then(() => setReceipt(receiptKey));
        }}/>}
      {data && (
        <DataTable
          rows={data}
          rowKey={(r) => r.id}
          columns={[
            { key: "num", header: "No.", render: (r) => pilot.data?.enabled ? <button className="text-brand-700 underline" onClick={() => {setParams({bill:r.id});setPay(false);}}>{r.docNumber ?? "View bill"}</button> : r.docNumber ?? "—" },
            { key: "vendor", header: "Vendor", render: (r) => r.vendorName },
            { key: "date", header: "Date", render: (r) => formatDate(r.txnDate) },
            { key: "due", header: "Due", render: (r) => formatDate(r.dueDate) },
            {
              key: "status",
              header: "Status",
              render: (r) => <StatusBadge status={r.status} />,
            },
            {
              key: "total",
              header: "Total",
              align: "right",
              render: (r) => <Money cents={r.total} currency={currency} />,
            },
            {
              key: "balance",
              header: "Balance",
              align: "right",
              render: (r) => <Money cents={r.balance} currency={currency} />,
            },
          ]}
        />
      )}
    </div>
  );
}
